import { LOCALE_TAGS, type Locale } from '@/lib/i18n'
import type { MarketUi } from '@/lib/market'

const ZERO_DECIMAL_CURRENCIES = new Set(['CLP'])
const CURRENCY_REGIONS: Record<string, string> = { CLP: 'CL', BRL: 'BR', USD: 'US' }
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/

const moneyFormats = new Map<string, Intl.NumberFormat>()

export function currencyExponent(currency: string): 0 | 2 {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase()) ? 0 : 2
}

function moneyFormat(currency: string, locale: Locale) {
  const key = `${locale}:${currency}`
  let format = moneyFormats.get(key)
  if (!format) {
    const exponent = currencyExponent(currency)
    const region = CURRENCY_REGIONS[currency.toUpperCase()]
    format = new Intl.NumberFormat(region ? `${locale}-${region}` : locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    })
    moneyFormats.set(key, format)
  }
  return format
}

export function formatMoney(amount: number, currency: string, locale: Locale): string {
  return moneyFormat(currency, locale)
    .format(amount / 10 ** currencyExponent(currency))
    .replace(/\xa0/g, ' ')
}

export function formatDate(iso: string, locale: Locale): string {
  const date = iso ? new Date(iso) : null
  if (!date || Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(LOCALE_TAGS[locale], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: DATE_ONLY.test(iso) ? 'UTC' : undefined,
  }).format(date)
}

export function plaqueDateText(iso: string, dateOrder: MarketUi['dateOrder']): string {
  if (!DATE_PREFIX.test(iso)) return ''
  const year = iso.slice(2, 4)
  const month = iso.slice(5, 7)
  const day = iso.slice(8, 10)
  return dateOrder === 'mdy' ? `${month}.${day}.${year}` : `${day}.${month}.${year}`
}
